import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../services/supabaseClient';
import { Plus, MapPin, DollarSign, BedDouble, Bath, Calendar, Image as ImageIcon, Trash2, CheckCircle, UploadCloud, X, Building, View, Edit2 } from 'lucide-react';

const geocodeAddressForDB = async (address: string, city: string) => {
  try {
    const query = encodeURIComponent(`${address}, ${city}, Quebec, Canada`);
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}`);
    const geodata = await res.json();
    
    if (geodata && geodata.length > 0) {
      return {
        lat: parseFloat(geodata[0].lat),
        lng: parseFloat(geodata[0].lon)
      };
    }
  } catch (error) {
    console.error("Erreur de géocodage lors de la création/modification", error);
  }
  return { lat: 46.8139, lng: -71.2080 };
};

export default function ListingManager() {
  const [listings, setListings] = useState<any[]>([]);
  const [buildings, setBuildings] = useState<any[]>([]); 
  
  // ÉTATS DE NAVIGATION DU COMPOSANT
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // ÉTATS DU FORMULAIRE (Partagés entre Création et Modification)
  const [selectedBuilding, setSelectedBuilding] = useState(''); 
  const [title, setTitle] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState(''); 
  const [rent, setRent] = useState('');
  const [bedrooms, setBedrooms] = useState('1');
  const [bathrooms, setBathrooms] = useState('1');
  const [availableFrom, setAvailableFrom] = useState('');
  const [description, setDescription] = useState('');
  const [virtualTourUrl, setVirtualTourUrl] = useState('');
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => { 
    fetchListings(); 
    fetchBuildings(); 
  }, []);

  const fetchListings = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('habitat_listings').select('*').eq('landlord_id', user.id).order('created_at', { ascending: false });
    if (data) setListings(data);
  };

  const fetchBuildings = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('habitat_buildings').select('*').eq('landlord_id', user.id);
    if (data) setBuildings(data);
  };

  const handleBuildingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const buildingId = e.target.value;
    setSelectedBuilding(buildingId);
    
    if (buildingId) {
      const b = buildings.find(x => x.id === buildingId);
      if (b) {
        if (b.city) setCity(b.city);
        if (b.address) setAddress(b.address);
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setUploadingImage(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const newImageUrls: string[] = [];

    for (const file of Array.from(e.target.files)) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Math.random()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage.from('habitat_public').upload(fileName, file);
      
      if (uploadError) {
        alert("Erreur d'upload : " + uploadError.message);
        continue;
      }
      
      const { data: { publicUrl } } = supabase.storage.from('habitat_public').getPublicUrl(fileName);
      newImageUrls.push(publicUrl);
    }

    setImages((prev) => [...prev, ...newImageUrls]);
    setUploadingImage(false);
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setTitle(''); setCity(''); setAddress(''); setRent(''); setBedrooms('1'); setBathrooms('1'); setAvailableFrom(''); setDescription(''); setImages([]); setSelectedBuilding(''); setVirtualTourUrl('');
    setIsCreating(false);
    setIsEditing(false);
    setEditingId(null);
  };

  // ✅ PRÉ-REMPLIR LE FORMULAIRE POUR LA MODIFICATION
  const startEditing = (listing: any) => {
    setTitle(listing.title || '');
    setCity(listing.city || '');
    setAddress(listing.address || '');
    setRent(listing.monthly_rent?.toString() || '');
    setBedrooms(listing.bedrooms?.toString() || '1');
    setBathrooms(listing.bathrooms?.toString() || '1');
    setAvailableFrom(listing.available_from || '');
    setDescription(listing.description || '');
    setVirtualTourUrl(listing.virtual_tour_url || '');
    setImages(listing.images || []);
    setSelectedBuilding(listing.building_id || '');
    
    setEditingId(listing.id);
    setIsEditing(true);
    setIsCreating(true); // On réutilise l'interface de création
  };

  // ✅ SAUVEGARDER (CRÉATION OU MODIFICATION)
  const handleSaveListing = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return setLoading(false);

    const finalImages = images.length > 0 ? images : ['https://images.unsplash.com/photo-1502672260266-1c1de2d9d000?auto=format&fit=crop&q=80&w=800'];

    const coords = await geocodeAddressForDB(address, city);

    const listingData = {
      landlord_id: user.id, 
      building_id: selectedBuilding ? selectedBuilding : null,
      title, 
      city, 
      address, 
      monthly_rent: parseInt(rent), 
      bedrooms: parseInt(bedrooms), 
      bathrooms: parseFloat(bathrooms), 
      available_from: availableFrom, 
      description, 
      images: finalImages,
      virtual_tour_url: virtualTourUrl,
      lat: coords.lat,
      lng: coords.lng 
    };

    if (isEditing && editingId) {
      // ✏️ UPDATE
      const { error } = await supabase.from('habitat_listings').update(listingData).eq('id', editingId);
      if (!error) {
        alert("✅ Annonce modifiée avec succès !");
        resetForm(); 
        fetchListings();
      } else {
        alert("Erreur lors de la modification : " + error.message);
      }
    } else {
      // ➕ INSERT (Création)
      const { error } = await supabase.from('habitat_listings').insert([listingData]);
      if (!error) {
        alert("✅ Annonce publiée !");
        resetForm(); 
        fetchListings();
      } else {
        alert("Erreur lors de la création : " + error.message);
      }
    }
    setLoading(false);
  };

  const deleteListing = async (id: string) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer définitivement cette annonce ?")) return;
    const { error } = await supabase.from('habitat_listings').delete().eq('id', id);
    if (!error) setListings(prev => prev.filter(l => l.id !== id));
    else alert("Erreur lors de la suppression : " + error.message);
  };

  if (isCreating || isEditing) {
    return (
      <div className="max-w-4xl mx-auto animate-in fade-in">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-black text-slate-900">
            {isEditing ? "Modifier l'annonce" : "Créer une annonce"}
          </h2>
          <button onClick={resetForm} className="text-slate-500 font-bold bg-white px-4 py-2 rounded-xl shadow-sm hover:text-slate-900 transition-colors">Annuler</button>
        </div>

        <form onSubmit={handleSaveListing} className="bg-white rounded-[2rem] p-8 shadow-xl border border-slate-200 space-y-6">
          
          <div className="bg-teal-50/50 p-6 rounded-2xl border border-teal-100">
            <label className="block text-xs font-black text-teal-700 uppercase mb-2 flex items-center gap-2"><Building size={16}/> Immeuble rattaché</label>
            <select value={selectedBuilding} onChange={handleBuildingChange} className="w-full p-4 bg-white rounded-xl border border-teal-200 font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 outline-none shadow-sm cursor-pointer">
              <option value="">-- Aucun immeuble (Maison indépendante) --</option>
              {buildings.map(b => (
                <option key={b.id} value={b.id}>{b.name} {b.address ? `(${b.address})` : ''}</option>
              ))}
            </select>
          </div>

          <div><label className="block text-xs font-black text-slate-400 uppercase mb-2">Titre de l'annonce</label><input required value={title} onChange={e => setTitle(e.target.value)} placeholder="Superbe 4 ½ près des services..." className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold focus:ring-2 focus:ring-teal-500 outline-none" /></div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 md:col-span-1"><label className="block text-xs font-black text-slate-400 uppercase mb-2">Loyer / mois ($)</label><input required type="number" value={rent} onChange={e => setRent(e.target.value)} className="w-full p-4 bg-slate-50 rounded-xl border font-bold focus:ring-2 focus:ring-teal-500 outline-none" /></div>
            <div className="col-span-2 md:col-span-1"><label className="block text-xs font-black text-slate-400 uppercase mb-2">Dispo le</label><input required type="date" value={availableFrom} onChange={e => setAvailableFrom(e.target.value)} className="w-full p-4 bg-slate-50 rounded-xl border font-bold focus:ring-2 focus:ring-teal-500 outline-none" /></div>
            <div className="col-span-1"><label className="block text-xs font-black text-slate-400 uppercase mb-2">Chambres</label><input required type="number" value={bedrooms} onChange={e => setBedrooms(e.target.value)} className="w-full p-4 bg-slate-50 rounded-xl border font-bold focus:ring-2 focus:ring-teal-500 outline-none" min="0" step="1" /></div>
            <div className="col-span-1"><label className="block text-xs font-black text-slate-400 uppercase mb-2">Salles de bain</label><input required type="number" value={bathrooms} onChange={e => setBathrooms(e.target.value)} className="w-full p-4 bg-slate-50 rounded-xl border font-bold focus:ring-2 focus:ring-teal-500 outline-none" min="0" step="0.5" /></div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="block text-xs font-black text-slate-400 uppercase mb-2">Ville</label><input required value={city} onChange={e => setCity(e.target.value)} placeholder="Ex: Québec" className="w-full p-4 bg-slate-50 rounded-xl border font-bold outline-none focus:ring-2 focus:ring-teal-500" /></div>
            
            {/* ✅ CORRECTION : Champ Adresse standard. Geocode se charge des coordonnées gratuitement */}
            <div>
                <label className="block text-xs font-black text-slate-400 uppercase mb-2">Adresse</label>
                <input 
                  required 
                  value={address} 
                  onChange={e => setAddress(e.target.value)} 
                  placeholder="Ex: 123 Rue Principale" 
                  className="w-full p-4 bg-slate-50 rounded-xl border font-bold text-slate-900 outline-none focus:ring-2 focus:ring-teal-500" 
                />
            </div>
          </div>

          <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100">
            <label className="block text-xs font-black text-blue-700 uppercase mb-2 flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center text-white"><View size={14}/></div> 
              Visite Virtuelle 3D (Optionnel)
            </label>
            <input 
              type="url"
              value={virtualTourUrl} 
              onChange={e => setVirtualTourUrl(e.target.value)} 
              placeholder="Lien Matterport, Kuula, Youtube 360..." 
              className="w-full p-4 bg-white rounded-xl border border-blue-200 font-bold text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
            />
          </div>

          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
            <label className="block text-xs font-black text-slate-400 uppercase mb-4 flex items-center gap-2"><ImageIcon size={14} className="text-teal-500"/> Galerie Photos</label>
            
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mb-4">
              {images.map((img, idx) => (
                <div key={idx} className="relative aspect-square rounded-xl overflow-hidden shadow-sm border border-slate-200 group">
                  <img src={img} className="w-full h-full object-cover" alt="Uploaded" />
                  <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X size={12}/></button>
                </div>
              ))}
            </div>

            <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-teal-300 rounded-xl cursor-pointer hover:bg-teal-50 transition-colors ${uploadingImage ? 'opacity-50' : ''}`}>
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <UploadCloud className="w-8 h-8 text-teal-500 mb-2" />
                <p className="text-sm font-bold text-teal-700">{uploadingImage ? 'Téléversement...' : 'Cliquez pour ajouter des photos'}</p>
                <p className="text-xs text-slate-500 mt-1">PNG, JPG, JPEG</p>
              </div>
              <input type="file" className="hidden" multiple accept="image/*" onChange={handleImageUpload} disabled={uploadingImage} />
            </label>
          </div>

          <div><label className="block text-xs font-black text-slate-400 uppercase mb-2">Description détaillée</label><textarea required value={description} onChange={e => setDescription(e.target.value)} className="w-full p-4 bg-slate-50 rounded-xl border font-bold outline-none h-32 resize-none focus:ring-2 focus:ring-teal-500" /></div>

          <button type="submit" disabled={loading} className="w-full bg-slate-900 text-white py-5 rounded-xl font-black uppercase tracking-widest hover:bg-teal-600 transition shadow-lg mt-4 flex justify-center gap-2">
            {loading ? 'Sauvegarde...' : isEditing ? <><CheckCircle size={18} /> Enregistrer les modifications</> : <><CheckCircle size={18} /> Publier l'annonce</>}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-black text-slate-900">Mes Annonces</h2>
        <button onClick={() => { resetForm(); setIsCreating(true); }} className="bg-teal-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-teal-500/30 flex items-center gap-2 hover:bg-teal-600 transition"><Plus size={18} /> Nouvelle</button>
      </div>

      {listings.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-300 rounded-[2rem] p-16 text-center text-slate-500 font-bold">Aucune annonce publiée.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map(l => (
            <div key={l.id} className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all flex flex-col h-full group">
              <div className="h-48 relative bg-slate-100 shrink-0">
                <img src={l.images[0]} alt={l.title} className="w-full h-full object-cover" />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-black text-teal-700 shadow-sm border border-teal-100">En ligne</div>
                {l.virtual_tour_url && (
                  <div className="absolute bottom-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-black flex items-center gap-1 shadow-md border border-blue-400">
                    <View size={12}/> 3D
                  </div>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-lg font-black text-slate-900 line-clamp-2 mb-2 leading-tight">{l.title}</h3>
                
                {/* Petit badge optionnel pour montrer l'adresse ou le statut */}
                <p className="text-xs text-slate-500 font-medium mb-4 flex items-center gap-1 truncate"><MapPin size={12}/> {l.address}, {l.city}</p>

                {/* Footer de la carte (Prix + Actions) */}
                <div className="mt-auto pt-4 border-t border-slate-100 flex justify-between items-center">
                  <div className="text-xl font-black text-slate-900">{l.monthly_rent} <span className="text-sm text-slate-400 font-bold">$ / mois</span></div>
                  
                  {/* ✅ NOUVEAUX BOUTONS D'ACTION (Modifier + Supprimer) */}
                  <div className="flex gap-1">
                    <button 
                      onClick={() => startEditing(l)} 
                      title="Modifier l'annonce"
                      className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-2.5 rounded-xl transition-colors border border-transparent hover:border-blue-100"
                    >
                      <Edit2 size={16} strokeWidth={2.5}/>
                    </button>
                    <button 
                      onClick={() => deleteListing(l.id)} 
                      title="Supprimer l'annonce"
                      className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-2.5 rounded-xl transition-colors border border-transparent hover:border-red-100"
                    >
                      <Trash2 size={16} strokeWidth={2.5}/>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
